/* eslint-disable @typescript-eslint/no-explicit-any -- form default values and error handlers */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { notificationTemplateApi } from '@/services/notificationApi';
import { useEntity, useMutation } from '@/hooks';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/Button';
import { LoadingState, ErrorState } from '@/components/common';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ArrowLeft, Save, Play, CheckCircle2, XCircle, Plus, Trash2, Copy } from 'lucide-react';
import type {
  NotificationTemplate,
  NotificationTemplateRequest,
  NotificationChannel,
  TemplateStatus,
  TemplateType,
} from '@/types/notification';

// Base schema with conditional validation
const templateSchema = z
  .object({
    name: z.string().min(1, 'Name is required').max(255, 'Name must be less than 255 characters'),
    description: z.string().max(1000, 'Description must be less than 1000 characters').optional(),
    channel: z.enum(['EMAIL', 'SMS', 'WHATSAPP', 'PUSH_NOTIFICATION', 'IN_APP']),
    locale: z.string().regex(/^[a-z]{2}$/, 'Locale must be a valid ISO 639-1 code (2 letters)'),
    status: z.enum(['DRAFT', 'ACTIVE', 'INACTIVE', 'ARCHIVED']),
    subject: z.string().max(500, 'Subject must be less than 500 characters').optional(),
    content: z.string().min(1, 'Content is required').max(10000, 'Content must be less than 10000 characters'),
    htmlContent: z.string().max(50000, 'HTML content must be less than 50000 characters').optional(),
    type: z.enum(['TEXT', 'HTML', 'MIXED']),
    defaultVariables: z.record(z.string(), z.unknown()).optional(),
    metadata: z.record(z.string(), z.string()).optional(),
  })
  .refine(
    (data) => {
      if (data.channel === 'EMAIL') {
        return data.subject && data.subject.length > 0;
      }
      return true;
    },
    {
      message: 'Subject is required for EMAIL channel',
      path: ['subject'],
    } as any
  )
  .refine(
    (data) => {
      if (data.type === 'HTML' || data.type === 'MIXED') {
        return data.htmlContent && data.htmlContent.length > 0;
      }
      return true;
    },
    {
      message: 'HTML content is required for HTML or MIXED type',
      path: ['htmlContent'],
    } as any
  );

type TemplateFormData = z.infer<typeof templateSchema>;

const CHANNELS: NotificationChannel[] = [
  'EMAIL',
  'SMS',
  'WHATSAPP',
  'PUSH_NOTIFICATION',
  'IN_APP',
];
const STATUSES: TemplateStatus[] = ['DRAFT', 'ACTIVE', 'INACTIVE', 'ARCHIVED'];
const TYPES: TemplateType[] = ['TEXT', 'HTML', 'MIXED'];
const COMMON_LOCALES = ['en', 'es', 'fr', 'de', 'it', 'pt', 'zh', 'ja', 'ko', 'ar'];

export const NotificationTemplateFormPage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = !!templateId;
  const cloneId = searchParams.get('clone');
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<{ valid: boolean; message?: string } | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewVariables, setPreviewVariables] = useState<Record<string, string>>({});
  const [previewOutput, setPreviewOutput] = useState<string>('');
  const [isPreviewing, setIsPreviewing] = useState(false);
  const { toast } = useToast();

  const form = useForm<TemplateFormData>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      name: '',
      description: '',
      channel: 'EMAIL',
      locale: 'en',
      status: 'DRAFT',
      subject: '',
      content: '',
      htmlContent: '',
      type: 'TEXT',
      defaultVariables: {},
      metadata: {},
    },
  });

  const [variableEntries, setVariableEntries] = useState<Array<{ key: string; value: string }>>([]);
  const [metadataEntries, setMetadataEntries] = useState<Array<{ key: string; value: string }>>([]);

  const watchedChannel = form.watch('channel');
  const watchedType = form.watch('type');
  const watchedContent = form.watch('content');
  const watchedHtmlContent = form.watch('htmlContent');

  // Clear errors when channel or type changes
  useEffect(() => {
    form.clearErrors();
  }, [watchedChannel, watchedType, form]);

  // Fetch template data for edit mode
  const {
    data: _template,
    isLoading: isLoadingTemplate,
    error: templateError,
  } = useEntity<NotificationTemplate>({
    fetchFn: (templateId: string) => notificationTemplateApi.getById(templateId),
    entityId: templateId || cloneId || undefined,
    entityName: 'notificationTemplate',
    enabled: (isEdit && !!templateId) || !!cloneId,
    onSuccess: (data) => {
      const defaultVars = data.defaultVariables || {};
      const metadata = data.metadata || {};
      form.reset({
        name: cloneId ? `${data.name} (Copy)` : data.name,
        description: data.description || '',
        channel: data.channel,
        locale: data.locale,
        status: cloneId ? 'DRAFT' : data.status,
        subject: data.subject || '',
        content: data.content,
        htmlContent: data.htmlContent || '',
        type: data.type,
        defaultVariables: cloneId ? { ...defaultVars } : defaultVars,
        metadata: cloneId ? { ...metadata } : metadata,
      });
      setVariableEntries(Object.entries(defaultVars).map(([key, value]) => ({ key, value: String(value) })));
      setMetadataEntries(Object.entries(metadata).map(([key, value]) => ({ key, value: String(value) })));
    },
    onError: () => {
      if (isEdit) {
        navigate('/admin/notifications/templates');
      }
    },
  });

  const extractVariables = useCallback((content: string): string[] => {
    const regex = /\$\{([^}]+)\}/g;
    const matches = content.matchAll(regex);
    const variables = new Set<string>();
    for (const match of matches) {
      const varName = match[1].split('.')[0].split('(')[0].trim();
      if (varName && !varName.startsWith('#')) {
        variables.add(varName);
      }
    }
    return Array.from(variables).sort();
  }, []);

  const handleValidate = async () => {
    if (!templateId && !isEdit) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Please save the template first before validating',
      });
      return;
    }

    const content = form.getValues('content');
    const htmlContent = form.getValues('htmlContent');

    try {
      setIsValidating(true);
      // For new templates, we'd need a validate endpoint that accepts raw content
      // For now, we'll validate on the client side or use the templateId if editing
      if (templateId) {
        // API expects a string (template content), so we validate the main content
        // For HTML/MIXED types, we could validate htmlContent separately if needed
        const contentToValidate = (watchedType === 'HTML' || watchedType === 'MIXED') && htmlContent 
          ? htmlContent 
          : content;
        const isValid = await notificationTemplateApi.validate(templateId, contentToValidate);
        setValidationResult({ valid: isValid });
        toast({
          variant: isValid ? 'success' : 'destructive',
          title: isValid ? 'Valid' : 'Invalid',
          description: isValid ? 'Template syntax is valid' : 'Template syntax error',
        });
      } else {
        // Client-side basic validation
        const hasUnclosed = content.includes('${') && !content.match(/\$\{[^}]+\}/);
        setValidationResult({
          valid: !hasUnclosed,
          message: hasUnclosed ? 'Unclosed Thymeleaf expression' : undefined,
        });
        toast({
          variant: hasUnclosed ? 'destructive' : 'success',
          title: hasUnclosed ? 'Invalid' : 'Valid',
          description: hasUnclosed ? 'Unclosed Thymeleaf expression detected' : 'Template syntax appears valid',
        });
      }
    } catch (error: unknown) {
      setValidationResult({ valid: false, message: (error as Error)?.message ?? 'Validation failed' });
      toast({
        variant: 'destructive',
        title: 'Validation Error',
        description: (error as Error)?.message ?? 'Failed to validate template',
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handlePreview = async () => {
    if (!templateId && !isEdit) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Please save the template first before previewing',
      });
      return;
    }

    if (!templateId) return;

    try {
      setIsPreviewing(true);
      // API expects variables directly, not wrapped in a 'variables' key
      const output = await notificationTemplateApi.test(templateId, previewVariables);
      setPreviewOutput(output);
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: (error as Error)?.message ?? 'Failed to preview template',
      });
    } finally {
      setIsPreviewing(false);
    }
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: TemplateFormData) => {
      const payload: NotificationTemplateRequest = {
        name: data.name,
        description: data.description,
        channel: data.channel,
        locale: data.locale,
        status: data.status,
        subject: data.subject,
        content: data.content,
        htmlContent: data.htmlContent,
        type: data.type,
        defaultVariables: data.defaultVariables || {},
        metadata: (data.metadata || {}) as Record<string, string>,
      };
      return await notificationTemplateApi.create(payload);
    },
    entityName: 'notificationTemplate',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/notifications/templates');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: TemplateFormData) => {
      if (!templateId) throw new Error('Template ID is required');
      const payload: NotificationTemplateRequest = {
        name: data.name,
        description: data.description,
        channel: data.channel,
        locale: data.locale,
        status: data.status,
        subject: data.subject,
        content: data.content,
        htmlContent: data.htmlContent,
        type: data.type,
        defaultVariables: data.defaultVariables || {},
        metadata: (data.metadata || {}) as Record<string, string>,
      };
      return await notificationTemplateApi.update(templateId, payload);
    },
    entityName: 'notificationTemplate',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/notifications/templates');
    },
  });

  const onSubmit = async (data: TemplateFormData) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const isLoading = isLoadingTemplate;
  const mutation = isEdit ? updateMutation : createMutation;

  const detectedVariables = [
    ...extractVariables(watchedContent || ''),
    ...(watchedHtmlContent ? extractVariables(watchedHtmlContent) : []),
  ];

  // Auto-populate preview variables from detected variables
  useEffect(() => {
    if (previewModalOpen && detectedVariables.length > 0) {
      const vars: Record<string, string> = {};
      const defaultVars = form.getValues('defaultVariables') || {};
      detectedVariables.forEach((varName) => {
        if (!previewVariables[varName]) {
          vars[varName] = defaultVars[varName] ? String(defaultVars[varName]) : '';
        } else {
          vars[varName] = previewVariables[varName];
        }
      });
      setPreviewVariables(vars);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally omit previewVariables to avoid loop (effect sets it)
  }, [previewModalOpen, detectedVariables, form]);

  // In edit/clone mode, show loading state while fetching
  if ((isEdit || cloneId) && isLoading) {
    return <LoadingState message="Loading template..." />;
  }

  // Show error if there's an error (only after loading completes)
  if (isEdit && templateError) {
    return <ErrorState error={templateError} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/notifications/templates">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEdit ? 'Edit Template' : cloneId ? 'Clone Template' : 'Create Template'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isEdit ? 'Update template information' : 'Create a new notification template'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleValidate} disabled={isValidating}>
            {isValidating ? 'Validating...' : 'Validate'}
          </Button>
          {isEdit && templateId && (
            <Button variant="outline" onClick={() => setPreviewModalOpen(true)}>
              <Play className="mr-2 h-4 w-4" />
              Preview
            </Button>
          )}
        </div>
      </div>

      {/* Validation Result */}
      {validationResult && (
        <Card
          className={
            validationResult.valid
              ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
              : 'border-red-500 bg-red-50 dark:bg-red-900/20'
          }
        >
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              {validationResult.valid ? (
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              )}
              <p
                className={`text-sm font-medium ${
                  validationResult.valid
                    ? 'text-green-800 dark:text-green-200'
                    : 'text-red-800 dark:text-red-200'
                }`}
              >
                {validationResult.valid
                  ? 'Template syntax is valid'
                  : validationResult.message || 'Template syntax error'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Template identification and classification</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter template name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter template description"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>Optional description (max 1000 characters)</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="channel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Channel *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select channel" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CHANNELS.map((channel) => (
                            <SelectItem key={channel} value={channel}>
                              {channel.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="locale"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Locale *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select locale" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {COMMON_LOCALES.map((locale) => (
                            <SelectItem key={locale} value={locale}>
                              {locale.toUpperCase()}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>ISO 639-1 language code</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Subject (EMAIL only) */}
          {watchedChannel === 'EMAIL' && (
            <Card>
              <CardHeader>
                <CardTitle>Email Subject</CardTitle>
                <CardDescription>Email subject line (required for EMAIL channel)</CardDescription>
              </CardHeader>
              <CardContent>
                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Subject *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter email subject" {...field} />
                      </FormControl>
                      <FormDescription>Supports Thymeleaf variables (e.g., {'${'}userName{'}'})</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          )}

          {/* Content */}
          <Card>
            <CardHeader>
              <CardTitle>Template Content</CardTitle>
              <CardDescription>
                Template content with Thymeleaf syntax (e.g., {'${'}variableName{'}'})
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Text Content *</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[200px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono"
                        placeholder="Enter template content with Thymeleaf syntax..."
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Use {'${'}variableName{'}'} for variables. Max 10000 characters.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {(watchedType === 'HTML' || watchedType === 'MIXED') && (
                <FormField
                  control={form.control}
                  name="htmlContent"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>HTML Content *</FormLabel>
                      <FormControl>
                        <textarea
                          className="flex min-h-[300px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono"
                          placeholder="Enter HTML content with Thymeleaf syntax..."
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        HTML content with Thymeleaf syntax. Max 50000 characters.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {detectedVariables.length > 0 && (
                <div className="space-y-2">
                  <Label>Detected Variables</Label>
                  <div className="flex flex-wrap gap-2">
                    {detectedVariables.map((variable) => (
                      <span
                        key={variable}
                        className="px-2 py-1 rounded-md bg-secondary text-secondary-foreground text-sm font-mono"
                      >
                        {`${variable}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Default Variables */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Default Variables</CardTitle>
                  <CardDescription>Default values for template variables</CardDescription>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setVariableEntries([...variableEntries, { key: '', value: '' }]);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Variable
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {variableEntries.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No default variables. Click "Add Variable" to add one.
                </div>
              ) : (
                variableEntries.map((entry, index) => (
                  <div key={index} className="grid gap-4 md:grid-cols-[1fr_1fr_auto]">
                    <Input
                      placeholder="Variable name"
                      value={entry.key}
                      onChange={(e) => {
                        const newEntries = [...variableEntries];
                        newEntries[index].key = e.target.value;
                        setVariableEntries(newEntries);
                        // Update form value
                        const vars: Record<string, unknown> = {};
                        newEntries.forEach((ent) => {
                          if (ent.key) {
                            vars[ent.key] = ent.value;
                          }
                        });
                        form.setValue('defaultVariables', vars);
                      }}
                    />
                    <Input
                      placeholder="Variable value"
                      value={entry.value}
                      onChange={(e) => {
                        const newEntries = [...variableEntries];
                        newEntries[index].value = e.target.value;
                        setVariableEntries(newEntries);
                        // Update form value
                        const vars: Record<string, unknown> = {};
                        newEntries.forEach((ent) => {
                          if (ent.key) {
                            vars[ent.key] = ent.value;
                          }
                        });
                        form.setValue('defaultVariables', vars);
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const newEntries = variableEntries.filter((_, i) => i !== index);
                        setVariableEntries(newEntries);
                        // Update form value
                        const vars: Record<string, unknown> = {};
                        newEntries.forEach((ent) => {
                          if (ent.key) {
                            vars[ent.key] = ent.value;
                          }
                        });
                        form.setValue('defaultVariables', vars);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Metadata</CardTitle>
                  <CardDescription>Additional metadata (key-value pairs)</CardDescription>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setMetadataEntries([...metadataEntries, { key: '', value: '' }]);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Metadata
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {metadataEntries.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No metadata. Click "Add Metadata" to add one.
                </div>
              ) : (
                metadataEntries.map((entry, index) => (
                  <div key={index} className="grid gap-4 md:grid-cols-[1fr_1fr_auto]">
                    <Input
                      placeholder="Metadata key"
                      value={entry.key}
                      onChange={(e) => {
                        const newEntries = [...metadataEntries];
                        newEntries[index].key = e.target.value;
                        setMetadataEntries(newEntries);
                        // Update form value
                        const meta: Record<string, string> = {};
                        newEntries.forEach((ent) => {
                          if (ent.key) {
                            meta[ent.key] = ent.value;
                          }
                        });
                        form.setValue('metadata', meta);
                      }}
                    />
                    <Input
                      placeholder="Metadata value"
                      value={entry.value}
                      onChange={(e) => {
                        const newEntries = [...metadataEntries];
                        newEntries[index].value = e.target.value;
                        setMetadataEntries(newEntries);
                        // Update form value
                        const meta: Record<string, string> = {};
                        newEntries.forEach((ent) => {
                          if (ent.key) {
                            meta[ent.key] = ent.value;
                          }
                        });
                        form.setValue('metadata', meta);
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const newEntries = metadataEntries.filter((_, i) => i !== index);
                        setMetadataEntries(newEntries);
                        // Update form value
                        const meta: Record<string, string> = {};
                        newEntries.forEach((ent) => {
                          if (ent.key) {
                            meta[ent.key] = ent.value;
                          }
                        });
                        form.setValue('metadata', meta);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/notifications/templates">Cancel</Link>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                form.setValue('status', 'DRAFT');
                form.handleSubmit(onSubmit)();
              }}
              disabled={mutation.isLoading}
            >
              Save as Draft
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Form>

      {/* Preview Modal */}
      <Dialog open={previewModalOpen} onOpenChange={setPreviewModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview Template</DialogTitle>
            <DialogDescription>
              Enter variable values to preview the rendered template
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Variables</Label>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {detectedVariables.length > 0 ? (
                  detectedVariables.map((variable) => (
                    <div key={variable} className="grid gap-2 md:grid-cols-2">
                      <Input
                        value={variable}
                        disabled
                        className="font-mono"
                        placeholder="Variable name"
                      />
                      <Input
                        value={previewVariables[variable] || ''}
                        onChange={(e) =>
                          setPreviewVariables({ ...previewVariables, [variable]: e.target.value })
                        }
                        placeholder="Variable value"
                      />
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No variables detected</p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handlePreview} disabled={isPreviewing || !templateId}>
                {isPreviewing ? 'Previewing...' : 'Preview'}
              </Button>
              {previewOutput && (
                <Button
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(previewOutput);
                    toast({
                      variant: 'success',
                      title: 'Copied',
                      description: 'Preview output copied to clipboard',
                    });
                  }}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy Output
                </Button>
              )}
            </div>
            {previewOutput && (
              <div className="space-y-2">
                <Label>Rendered Output</Label>
                <textarea
                  className="flex min-h-[200px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                  value={previewOutput}
                  readOnly
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationTemplateFormPage;








